/**
 * Single source of truth for serializing Card Settings for dirty detection.
 *
 * INVARIANT (do not violate):
 *   Both the snapshot (built from the API card object) AND the live settings
 *   (built from React state) MUST be produced by this exact same function.
 *
 *   Adding a new card setting = add ONE entry to FIELDS below. Both sides
 *   are then guaranteed to stay in sync forever — no more phantom "Pending
 *   Changes" caused by a key mismatch or type drift.
 *
 *   See PRD v2.15.4. Replaces the parallel `buildCardSettingsSnapshot` /
 *   `currentCardSettings` formulas that previously had to be hand-maintained
 *   in lockstep.
 */

// ── Coercion helpers ──

const _toIntOr = (v, d) => {
    const n = parseInt(v);
    return Number.isFinite(n) ? n : d;
};

const _toBool = (v) => Boolean(v);
// "Default-true" boolean: missing/undefined → true; explicit false → false.
const _toBoolDefaultTrue = (v) => v !== false;

const _toArray = (v) => (Array.isArray(v) ? v : []);
const _toObject = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
const _toStr = (v, d = '') => (v === null || v === undefined ? d : String(v));
const _orNull = (v) => (v === undefined ? null : v);

// ── Field registry — ONE place ──
//
// Each entry: {
//   key:       camelCase canonical key (used in JSON output & React state)
//   card:      snake_case key on the card object returned by the API
//   type:      coercion bucket (see normalize() below)
//   default:   fallback value if the source is missing/null/invalid
// }
//
// To add a new setting: append one row here. That's it.
const FIELDS = [
    { key: 'interactionMode',                 card: 'interaction_mode',                  type: 'str',     default: 'standard'         },
    { key: 'landingPageEnabled',              card: 'landing_page_enabled',              type: 'bool',    default: false              },
    { key: 'landingShowText',                 card: 'landing_show_text',                 type: 'boolDT', default: true               },
    { key: 'landingHeadline',                 card: 'landing_headline',                  type: 'str',     default: ''                 },
    { key: 'landingDescription',              card: 'landing_description',               type: 'str',     default: ''                 },
    { key: 'landingLogoUrl',                  card: 'landing_logo_url',                  type: 'str',     default: ''                 },
    { key: 'landingLogoSize',                 card: 'landing_logo_size',                 type: 'str',     default: 'medium'           },
    { key: 'landingAutoRedirect',             card: 'landing_auto_redirect',             type: 'boolDT', default: true               },
    { key: 'landingRedirectDelay',            card: 'landing_redirect_delay',            type: 'int',     default: 5                  },
    { key: 'countdownClockEnabled',           card: 'countdown_clock_enabled',           type: 'boolDT', default: true               },
    { key: 'countdownClockColor',             card: 'countdown_clock_color',             type: 'str',     default: 'blue'             },
    { key: 'showYulontapBranding',            card: 'show_yulontap_branding',            type: 'boolDT', default: true               },
    { key: 'multipleAdsEnabled',              card: 'multiple_ads_enabled',              type: 'bool',    default: false              },
    { key: 'adImages',                        card: 'ad_images',                         type: 'array',   default: []                 },
    { key: 'sameTextForAll',                  card: 'same_text_for_all',                 type: 'boolDT', default: true               },
    { key: 'adTexts',                         card: 'ad_texts',                          type: 'array',   default: []                 },
    { key: 'scheduleEnabled',                 card: 'schedule_enabled',                  type: 'bool',    default: false              },
    { key: 'scheduleTimezone',                card: 'schedule_timezone',                 type: 'str',     default: 'America/New_York' },
    { key: 'scheduleEntries',                 card: 'schedule_entries',                  type: 'array',   default: []                 },
    { key: 'reviewShieldLogoUrl',             card: 'review_shield_logo_url',            type: 'str',     default: ''                 },
    { key: 'reviewShieldLogoSize',            card: 'review_shield_logo_size',           type: 'str',     default: 'medium'           },
    { key: 'reviewShieldBackgroundImageUrl',  card: 'review_shield_background_image_url', type: 'str',    default: ''                 },
    { key: 'cardStyle',                       card: 'card_style',                        type: 'str',     default: ''                 },
    { key: 'languageMode',                    card: 'language_mode',                     type: 'str',     default: 'bilingual'        },

    { key: 'enabledLanguages',                card: 'enabled_languages',                 type: 'arrayOrNull',default: null            },
    // Per-card Lead Generation override (Gift Generator). null = inherit org.
    { key: 'leadGenerationOverride',          card: 'lead_generation_override',          type: 'nullable',default: null               },
    // Which named Gift Generator setup the card runs. null = org default.
    { key: 'giftGeneratorSetupId',            card: 'gift_generator_setup_id',           type: 'nullable',default: null               },
    { key: 'reviewShieldQuestions',           card: 'review_shield_questions',           type: 'array',   default: []                 },
    { key: 'publicReviewThreshold',           card: 'public_review_threshold',           type: 'int',     default: 4                  },
    // Private Review — ON keeps every rating in house (no public review link).
    { key: 'privateReviewEnabled',            card: 'private_review_enabled',            type: 'bool',    default: false              },
    // v4.40.2 — Experience type was missing from the FIELDS registry, which
    // meant changing the Card Settings dropdown never marked the form dirty
    // and Save Changes never enabled. Added per the registry's own contract
    // ("Adding a new card setting = add ONE entry to FIELDS below").
    { key: 'experienceType',                  card: 'experience_type',                   type: 'str',     default: 'dine_in'          },
    { key: 'guestExperienceVersion',          card: 'guest_experience_version',          type: 'str',     default: 'v1'               },
    { key: 'timingEnabled',                   card: 'timing_enabled',                    type: 'bool',    default: false              },
    { key: 'timingSchedule',                  card: 'timing_schedule',                   type: 'array',   default: []                 },
    { key: 'notificationEmails',              card: 'notification_emails',               type: 'array',   default: []                 },
    { key: 'notificationPhones',              card: 'notification_phones',               type: 'array',   default: []                 },
    { key: 'directResponseEnabled',           card: 'direct_response_enabled',           type: 'bool',    default: false              },
    { key: 'requireEmployeePin',              card: 'require_employee_pin',              type: 'boolDT', default: true               },
    { key: 'reviewCounterEnabled',            card: 'review_counter_enabled',            type: 'bool',    default: false              },
    // P_TAP_PROTECTION_1.0 — canonical Tap Protection fields
    { key: 'tapProtectionEnabled',            card: 'tap_protection_enabled',            type: 'bool',    default: true               },
    { key: 'testMode',                        card: 'test_mode',                        type: 'bool',    default: false              },
    { key: 'duplicateProtectionEnabled',      card: 'duplicate_protection_enabled',      type: 'bool',    default: true               },
    { key: 'duplicateWindowSeconds',          card: 'duplicate_window_seconds',          type: 'int',     default: 30                 },
    { key: 'dwellProtectionEnabled',          card: 'dwell_protection_enabled',          type: 'bool',    default: true               },
    { key: 'dwellThresholdSeconds',           card: 'dwell_threshold_seconds',           type: 'int',     default: 10                 },
    { key: 'giftCardEnabled',                 card: 'gift_card_enabled',                 type: 'bool',    default: false              },
    // Loyal Review — additive card-level fields (see backend/routes/loyal_review_public.py).
    { key: 'loyalReviewEnabled',              card: 'loyal_review_enabled',              type: 'bool',    default: false              },
    { key: 'loyalReviewVoucherId',            card: 'loyal_review_voucher_id',           type: 'str',     default: ''                 },
    { key: 'loyalReviewAutoFlow',             card: 'loyal_review_auto_flow',            type: 'bool',    default: false              },
    { key: 'globalReviewEnabled',             card: 'global_review_enabled',             type: 'bool',    default: false              },
    { key: 'useReviewShieldWithGlobalReview', card: 'use_review_shield_with_global_review', type: 'bool', default: false             },
    { key: 'globalReviewDestinations',        card: 'global_review_destinations',        type: 'object',  default: {}                 },
    { key: 'globalReviewLogoUrl',             card: 'global_review_logo_url',            type: 'str',     default: ''                 },
    { key: 'globalReviewLogoSize',            card: 'global_review_logo_size',           type: 'str',     default: 'medium'           },
    { key: 'globalReviewBackgroundImageUrl',  card: 'global_review_background_image_url', type: 'str',    default: ''                 },
    { key: 'globalReviewMessageEn',           card: 'global_review_message_en',          type: 'str',     default: ''                 },

    { key: 'globalReviewMessageFr',           card: 'global_review_message_fr',          type: 'str',     default: ''                 },
    { key: 'hotelOpsLogoUrl',                 card: 'hotel_ops_logo_url',                type: 'str',     default: ''                 },
    { key: 'hotelOpsLogoSize',                card: 'hotel_ops_logo_size',               type: 'str',     default: 'medium'           },
    { key: 'hotelOpsBackgroundImageUrl',      card: 'hotel_ops_background_image_url',     type: 'str',    default: ''                 },
    { key: 'guestServicesContext',            card: 'guest_services_context',            type: 'str',     default: 'standard'         },
    { key: 'poolDrinksEnabled',               card: 'pool_drinks_enabled',               type: 'bool',    default: true               },
    { key: 'poolFoodEnabled',                 card: 'pool_food_enabled',                 type: 'bool',    default: true               },
    { key: 'poolTowelsEnabled',               card: 'pool_towels_enabled',               type: 'bool',    default: true               },
    { key: 'poolOtherEnabled',                card: 'pool_other_enabled',                type: 'bool',    default: true               },
    { key: 'poolDrinkMenuImageUrl',           card: 'pool_drink_menu_image_url',         type: 'str',     default: ''                 },
    { key: 'poolFoodMenuImageUrl',            card: 'pool_food_menu_image_url',          type: 'str',     default: ''                 },
    { key: 'landingVideoEnabled',             card: 'landing_video_enabled',             type: 'bool',    default: false              },
    { key: 'mediaType',                       card: 'media_type',                        type: 'nullable',default: null               },
    { key: 'mediaVideoUrl',                   card: 'media_video_url',                   type: 'str',     default: ''                 },
    { key: 'mediaVideoId',                    card: 'media_video_id',                    type: 'str',     default: ''                 },
    { key: 'mediaVideoPosterUrl',             card: 'media_video_poster_url',            type: 'str',     default: ''                 },
    { key: 'mediaVideoFallbackImage',         card: 'media_video_fallback_image',        type: 'str',     default: ''                 },
    { key: 'mediaAutoplay',                   card: 'media_autoplay',                    type: 'boolDT', default: true               },
    { key: 'mediaLoop',                       card: 'media_loop',                        type: 'bool',    default: false              },
    { key: 'mediaMuted',                      card: 'media_muted',                       type: 'boolDT', default: true               },
    { key: 'mediaSkipEnabled',                card: 'media_skip_enabled',                type: 'boolDT', default: true               },
    { key: 'mediaSkipDelay',                  card: 'media_skip_delay',                  type: 'int',     default: 5                  },
];

function normalize(v, type, def) {
    switch (type) {
        case 'int':      return _toIntOr(v, def);
        case 'bool':     return _toBool(v);
        case 'boolDT':   return _toBoolDefaultTrue(v);
        case 'array':    return _toArray(v);
        case 'arrayOrNull': return v == null ? null : _toArray(v);
        case 'object':   return _toObject(v);
        case 'str':      return _toStr(v, def);
        case 'nullable': return _orNull(v);
        default:         return v ?? def;
    }
}

/**
 * Serialize either a card object (from the API) or a flat state object
 * (from React useState values keyed by camelCase) to a stable JSON string.
 *
 * @param {Object} input  Either { interaction_mode, landing_page_enabled, ... }
 *                        OR     { interactionMode,  landingPageEnabled, ... }
 * @param {'card'|'state'} source  Tells the function which key style to read.
 * @returns {string} JSON string suitable for === comparison.
 */
export function serializeCardSettings(input, source = 'card') {
    if (!input || typeof input !== 'object') return JSON.stringify({});

    // ── P_PORTAL_SAVE_INTEGRITY_AUDIT — dev-time coverage guard ──
    // Catches the class of bug that hid behind the "Override for this"
    // language picker: a registry FIELD whose key is missing from the
    // state bag silently normalizes to its default, so dirty-detection
    // never sees the user's change and Save Changes never enables.
    // In production this is a no-op (process.env.NODE_ENV === 'production'
    // strips dev branches under CRA).
    if (
        source === 'state'
        && typeof process !== 'undefined'
        && process.env
        && process.env.NODE_ENV !== 'production'
    ) {
        const missing = [];
        for (const f of FIELDS) {
            if (!Object.prototype.hasOwnProperty.call(input, f.key)) {
                missing.push(f.key);
            }
        }
        if (missing.length > 0 && !serializeCardSettings._warnedKeys) {
            serializeCardSettings._warnedKeys = new Set();
        }
        for (const k of missing) {
            if (serializeCardSettings._warnedKeys && !serializeCardSettings._warnedKeys.has(k)) {
                serializeCardSettings._warnedKeys.add(k);
                // eslint-disable-next-line no-console
                console.error(
                    `[SAVE_INTEGRITY] serializeCardSettings(state) missing key: "${k}". ` +
                    `Dirty-detection will never fire for this field — Save Changes will ` +
                    `stay disabled when only "${k}" is edited. Add it to the state bag ` +
                    `in Cards.jsx (cardSettingsStateBag).`
                );
            }
        }
    }

    const out = {};
    for (const f of FIELDS) {
        const raw = source === 'card' ? input[f.card] : input[f.key];
        out[f.key] = normalize(raw, f.type, f.default);
    }
    return JSON.stringify(out);
}

/** Convenience export for the diff display in Cards.jsx. Each entry
 *  is the camelCase key the diff layer uses. Anyone iterating these
 *  is guaranteed to see the same keys present in the JSON output. */
export const CARD_SETTING_KEYS = FIELDS.map(f => f.key);
